import { Request } from 'api';
import { AxiosResponse } from 'axios';
import { Fp1Contract, CreateContractParams, UpdateContractParams } from './contracts.types';

interface PaginatedResponse<T> {
    data: Array<T>;
    links: any;
    meta: {
        total: number;
        current_page: number;
        per_page: number;
        last_page?: number;
    };
}

export interface GetContractsParams {
    search?: string;
    status?: string;
    organization_uuid?: string;
    discount_type?: string;
    expiring_soon?: boolean;
    include_archived?: boolean;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    page?: number;
    per_page?: number;
}

const getContracts = (
    params?: GetContractsParams,
): Promise<AxiosResponse<PaginatedResponse<Fp1Contract>>> => {
    const p: Record<string, any> = {};
    if (params?.search) p.search = params.search;
    if (params?.status) p.status = params.status;
    if (params?.organization_uuid) p.organization_uuid = params.organization_uuid;
    if (params?.discount_type) p.discount_type = params.discount_type;
    if (params?.expiring_soon) p.expiring_soon = 1;
    if (params?.include_archived) p.include_archived = 1;
    if (params?.sort_by) p.sort_by = params.sort_by;
    if (params?.sort_dir) p.sort_dir = params.sort_dir;
    if (params?.page) p.page = params.page;
    if (params?.per_page) p.per_page = params.per_page;
    return Request.get('admin/fp1/contracts', { params: p });
};

const getContract = (uuid: string): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    return Request.get(`admin/fp1/contracts/${uuid}`);
};

const createContract = (
    data: CreateContractParams,
): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    return Request.post('admin/fp1/contracts', data);
};

const updateContract = (
    uuid: string,
    data: UpdateContractParams,
): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    return Request.put(`admin/fp1/contracts/${uuid}`, data);
};

const archiveContract = (uuid: string): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    return Request.post(`admin/fp1/contracts/${uuid}/archive`, {});
};

const duplicateContract = (uuid: string): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    return Request.post(`admin/fp1/contracts/${uuid}/duplicate`, {});
};

const uploadContractPdf = (
    uuid: string,
    file: File,
): Promise<AxiosResponse<{ data: Fp1Contract }>> => {
    const form = new FormData();
    form.append('pdf', file);
    return Request.post(`admin/fp1/contracts/${uuid}/upload-pdf`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

const getContractPdfDownloadUrl = (uuid: string): string => {
    return `admin/fp1/contracts/${uuid}/download-pdf`;
};

const getExpiringContracts = (
    days?: number,
): Promise<AxiosResponse<PaginatedResponse<Fp1Contract>>> => {
    return Request.get('admin/fp1/contracts/expiring', { params: days ? { days } : {} });
};

export {
    getContracts,
    getContract,
    createContract,
    updateContract,
    archiveContract,
    duplicateContract,
    uploadContractPdf,
    getContractPdfDownloadUrl,
    getExpiringContracts,
};
